import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Check, X, Edit3, Loader2, Mic, Camera } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtRelativeDate, fmtTime } from "@/lib/format";
import { MicButton } from "@/components/MicButton";
import { BottomSheet } from "@/components/BottomSheet";
import { MemberAvatar } from "@/components/Atoms";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const COMPLEX_THRESHOLD = 80;
const isComplex = (s) => {
  const t = (s || "").trim();
  if (t.length >= COMPLEX_THRESHOLD) return true;
  if (/[.!?]\s+\S/.test(t)) return true; // multi-sentence
  if ((t.match(/,/g) || []).length >= 2) return true; // list-like
  return false;
};

/**
 * Kinly Quick-Add input — natural-language → draft → accept/discard.
 * Simple inputs review inline. Complex/voice inputs open Plan-with-Kinly sheet.
 */
export function QuickAddBar({ familyId, onCommitted }) {
  const { members: ctxMembers } = useAuth();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  // Members from shared AuthContext (no per-screen fetch). Defaults to []
  // until context hydrates from IDB or bootstrap.
  const members = ctxMembers || [];
  const fileInputRef = useRef(null);

  const submitImage = async (file) => {
    if (!file || loading) return;
    setLoading(true);
    setError("");
    setDraft(null);
    setSheetOpen(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "screenshot.jpg");
      if (text.trim()) fd.append("caption", text.trim());
      fd.append("context_surface", "kinly_bar");
      const { data } = await api.post(
        `/families/${familyId}/kinly/quick-add-from-image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDraft(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (f) submitImage(f);
    e.target.value = ""; // allow re-picking the same file
  };

  const submitText = async (input, { forceSheet = false } = {}) => {
    if (!input.trim() || loading) return;
    const useSheet = forceSheet || isComplex(input);
    setLoading(true);
    setError("");
    setDraft(null);
    if (useSheet) setSheetOpen(true);
    try {
      const { data } = await api.post(`/families/${familyId}/kinly/quick-add`, {
        input: input.trim(),
        context_surface: "calendar",
      });
      setDraft(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    submitText(text);
  };

  const onVoiceTranscript = (t) => {
    setText(t);
    submitText(t, { forceSheet: true });
  };

  const onAccept = async () => {
    if (!draft?.draft_id) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        `/families/${familyId}/kinly/quick-add/${draft.draft_id}/accept`,
        {}
      );
      // Surface an inline toast — if the draft created an Activity row,
      // show "<N> sessions added to your calendar". Otherwise a generic
      // "Added <N> things" confirmation.
      const events = data.committed_events || [];
      const activities = events.filter(
        (e) => Array.isArray(e?.recurrence_template) || typeof e?._generated_count === "number"
      );
      if (activities.length > 0) {
        const totalSessions = activities.reduce(
          (acc, a) => acc + (a._generated_count || 0), 0,
        );
        const a0 = activities[0];
        if (totalSessions > 0) {
          toast.success(
            `${a0.name} added — ${totalSessions} session${totalSessions === 1 ? "" : "s"} on your calendar.`,
            { duration: 5000 },
          );
        } else {
          toast.success(`${a0.name} added.`);
        }
      } else if (data.count > 0) {
        toast.success(`Added ${data.count} ${data.count === 1 ? "thing" : "things"} to the family book.`);
      }
      setText("");
      setDraft(null);
      setSheetOpen(false);
      onCommitted?.(events);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onRefine = async (refinementText) => {
    if (!draft?.draft_id || !refinementText.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(
        `/families/${familyId}/kinly/quick-add/${draft.draft_id}/refine`,
        {
          input: refinementText.trim(),
          actions: draft.actions || [],
          original_input: text,
        }
      );
      setDraft((d) => ({ ...d, actions: data.actions, message: data.message }));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onDiscard = async () => {
    if (draft?.draft_id) {
      try { await api.post(`/families/${familyId}/kinly/quick-add/${draft.draft_id}/discard`); } catch (_e) { /* noop */ }
    }
    setText("");
    setDraft(null);
    setSheetOpen(false);
  };

  const onEdit = () => {
    setSheetOpen(true);
  };

  return (
    <div data-testid="quick-add-bar" className="space-y-2">
      <form
        onSubmit={onSubmit}
        className="flex items-center gap-2 bg-card border border-border rounded-2xl px-3 py-2 shadow-sm"
      >
        <Sparkles className="w-4 h-4 text-primary shrink-0" />
        <input
          data-testid="quick-add-input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='Brain-dump: "swim 3pm, pick up kids, groceries — bread milk eggs"'
          disabled={loading}
          className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/60"
        />
        <MicButton onTranscript={onVoiceTranscript} familyId={familyId} recordingId="quickadd-inline" />
        <button
          type="button"
          data-testid="quick-add-image"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
          aria-label="Attach a screenshot"
          title="Attach a screenshot — Kinly will read the text and turn it into events / activities."
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary tap text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
        >
          <Camera className="w-4 h-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickImage}
          className="hidden"
          data-testid="quick-add-image-input"
        />
        <button
          data-testid="quick-add-submit"
          type="submit"
          disabled={!text.trim() || loading}
          className="text-xs font-medium text-primary disabled:text-muted-foreground tap px-2 py-1"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask Kinly"}
        </button>
      </form>

      {error && (
        <p data-testid="quick-add-error" className="text-xs text-destructive px-1">
          {error}
        </p>
      )}

      {/* Answer-mode card — no Accept/Discard, just Kinly's reply. */}
      {draft && draft.mode === "answer" && !sheetOpen && (
        <article
          data-testid="quick-add-answer-card"
          className="kinly-card border-primary/20 bg-gradient-to-br from-[hsl(200_69%_98%)] to-card dark:from-[hsl(200_30%_18%)]"
        >
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p data-testid="quick-add-answer-text" className="text-[14px] text-foreground/95 leading-relaxed flex-1 whitespace-pre-wrap">
              {draft.message}
            </p>
            <button
              data-testid="quick-add-answer-dismiss"
              onClick={() => { setDraft(null); setText(""); }}
              className="text-xs font-medium text-muted-foreground hover:text-foreground tap"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </article>
      )}

      {/* Inline draft card — only for simple inputs (sheet handles complex) */}
      {draft && draft.mode !== "answer" && !sheetOpen && (
        <article
          data-testid="quick-add-draft-card"
          className="kinly-card border-primary/20 bg-gradient-to-br from-[hsl(12_69%_98%)] to-card dark:from-[hsl(12_30%_18%)]"
        >
          <div className="flex items-start gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-[14px] text-foreground/90 leading-relaxed flex-1">
              {draft.message}
            </p>
          </div>

          {(draft.actions || []).length > 0 ? (
            <GroupedActions actions={draft.actions} members={members} />
          ) : (
            <p className="text-xs text-muted-foreground italic mb-4">
              Kinly needs a bit more — try rephrasing.
            </p>
          )}

          {(draft.actions || []).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <button
                data-testid="quick-add-accept"
                onClick={onAccept}
                disabled={loading}
                className="h-10 rounded-xl bg-foreground text-background text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
              >
                <Check className="w-4 h-4" /> Accept
              </button>
              <button
                data-testid="quick-add-edit"
                onClick={onEdit}
                className="h-10 rounded-xl border border-border text-foreground text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
                title="Open Plan with Kinly"
              >
                <Edit3 className="w-4 h-4" /> Edit
              </button>
              <button
                data-testid="quick-add-discard"
                onClick={onDiscard}
                className="h-10 rounded-xl border border-border text-foreground text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
              >
                <X className="w-4 h-4" /> Discard
              </button>
            </div>
          )}
        </article>
      )}

      <PlanWithKinlySheet
        open={sheetOpen}
        familyId={familyId}
        text={text}
        draft={draft}
        members={members}
        loading={loading}
        onClose={onDiscard}
        onAccept={onAccept}
        onRetry={() => submitText(text, { forceSheet: true })}
        onRefine={onRefine}
        onTextChange={setText}
        onVoiceTranscript={onVoiceTranscript}
      />
    </div>
  );
}

// ──────────────────────── Plan with Kinly — large sheet ────────────────────────

function PlanWithKinlySheet({
  open, familyId, text, draft, members, loading,
  onClose, onAccept, onRetry, onRefine, onTextChange, onVoiceTranscript,
}) {
  const [refineText, setRefineText] = useState("");
  if (!open) return null;

  const onRefineSubmit = (e) => {
    e?.preventDefault();
    if (!refineText.trim() || loading) return;
    onRefine(refineText);
    setRefineText("");
  };
  const onRefineVoice = (t) => {
    setRefineText(t);
    // auto-fire so voice refinement is one tap
    setTimeout(() => { if (t.trim()) onRefine(t); setRefineText(""); }, 50);
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Plan with Kinly" fullHeight>
      <div data-testid="plan-with-kinly-sheet" className="flex flex-col gap-4 pb-4">
        {/* Original transcript */}
        <div className="space-y-1.5">
          <h4 className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">You said</h4>
          <textarea
            data-testid="plan-with-kinly-input"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            rows={Math.max(3, Math.min(6, (text.match(/\n/g) || []).length + 3))}
            placeholder="Type or speak — Kinly will sort it for you."
            className="w-full bg-card border border-border rounded-2xl px-4 py-3 text-[15px] outline-none focus:border-primary/60 resize-none"
          />
          {!draft && (
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="plan-with-kinly-retry"
                onClick={onRetry}
                disabled={loading || !text.trim()}
                className="h-9 px-4 rounded-full bg-foreground text-background text-sm font-medium tap inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Ask Kinly
              </button>
            </div>
          )}
        </div>

        {/* Kinly's response */}
        {draft && (
          <div className="space-y-2.5">
            <div className="flex items-start gap-2 pt-2 border-t border-border/60">
              <Sparkles className="w-4 h-4 text-primary mt-1 shrink-0" />
              <p className="text-[14px] leading-relaxed flex-1">{draft.message}</p>
            </div>
            {(draft.actions || []).length > 0 ? (
              <GroupedActions actions={draft.actions} members={members} verbose />
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Kinly didn&apos;t find anything to add — try refining below.
              </p>
            )}
          </div>
        )}

        {/* Refinement input — only when a draft exists */}
        {draft && (
          <form
            onSubmit={onRefineSubmit}
            className="bg-card border border-border rounded-2xl px-3 py-2 flex items-center gap-2"
            data-testid="plan-with-kinly-refine"
          >
            <MicButton onTranscript={onRefineVoice} familyId={familyId} recordingId="quickadd-refine" />
            <input
              data-testid="plan-with-kinly-refine-input"
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              placeholder='Refine — "move swim to 4, drop pickup"'
              className="flex-1 bg-transparent outline-none text-[14px]"
            />
            <button
              data-testid="plan-with-kinly-refine-submit"
              type="submit"
              disabled={loading || !refineText.trim()}
              className="text-xs font-medium text-primary disabled:text-muted-foreground tap px-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
            </button>
          </form>
        )}

        {/* Action footer */}
        {draft && (draft.actions || []).length > 0 && (
          <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-gradient-to-t from-card via-card to-card/80 border-t border-border/60 flex items-center gap-2">
            <button
              data-testid="plan-with-kinly-accept"
              onClick={onAccept}
              disabled={loading}
              className="flex-1 h-12 rounded-2xl bg-foreground text-background font-medium tap inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> Accept all
            </button>
            <button
              data-testid="plan-with-kinly-discard"
              onClick={onClose}
              className="h-12 px-5 rounded-2xl border border-border text-foreground font-medium tap inline-flex items-center gap-2"
            >
              <X className="w-4 h-4" /> Discard
            </button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

export function GroupedActions({ actions, members = [], verbose = false }) {
  const groups = [
    { key: "events", label: "Events", filter: (a) => a.type === "create_time_item" },
    { key: "activities", label: "Activities", filter: (a) => a.type === "create_activity" },
    { key: "places", label: "Places", filter: (a) => a.type === "create_place" },
    { key: "chores", label: "Chores", filter: (a) => a.type === "create_chore" },
    { key: "todos",  label: "To-dos", filter: (a) => a.type === "create_todo" },
    { key: "polls",  label: "Polls",  filter: (a) => a.type === "create_poll" },
  ];
  return (
    <div className="mb-4 space-y-3">
      {groups.map((g) => {
        const items = actions.map((a, idx) => ({ a, idx })).filter(({ a }) => g.filter(a));
        if (items.length === 0) return null;
        return (
          <section key={g.key} data-testid={`quick-add-group-${g.key}`}>
            <h4 className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-1.5">
              {g.label} · {items.length}
            </h4>
            <ul className="space-y-1.5">
              {items.map(({ a, idx }) => (
                <ActionCard key={idx} action={a} dataIdx={idx} members={members} verbose={verbose} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function ActionCard({ action, dataIdx, members = [], verbose = false }) {
  const p = action.payload || {};
  const t = action.type;

  // Resolve assignee names from display_names against family roster
  const resolveNames = (names) => (names || [])
    .map((n) => members.find((m) => (m.display_name || "").toLowerCase().includes(String(n).toLowerCase())))
    .filter(Boolean);
  const participants = resolveNames(p.participant_display_names);
  const assignees = resolveNames(p.assignee_display_names);
  const todoAssignee = members.find((m) =>
    (m.display_name || "").toLowerCase() === String(p.assignee_display_name || "").toLowerCase()
  );

  return (
    <li
      data-testid={`quick-add-action-${dataIdx}`}
      className="text-[13px] py-2 px-3 rounded-lg bg-secondary/50"
    >
      <div className="font-medium text-foreground">
        {p.title || p.question || p.name}
      </div>
      {t === "create_time_item" && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {fmtRelativeDate(p.start_at)} · {fmtTime(p.start_at)}
          {p.location_name ? ` · ${p.location_name}` : ""}
        </div>
      )}
      {t === "create_activity" && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {(() => {
            const W = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            const slots = p.recurrence_template || [];
            const days = [...new Set(slots.map((s) => W[s.weekday]).filter(Boolean))];
            const times = [...new Set(slots.map((s) => s.start_time).filter(Boolean))];
            const sessionCount = slots.length;
            const where = p.location_default || slots.find((s) => s.location)?.location;
            return [
              days.join("/") || "Recurring",
              times.length === 1 ? times[0] : null,
              where || null,
              sessionCount > 0 ? `${sessionCount} weekly slot${sessionCount === 1 ? "" : "s"}` : null,
            ].filter(Boolean).join(" · ");
          })()}
        </div>
      )}
      {t === "create_place" && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {p.category || "place"}
          {p.alert_radius_m ? ` · ${p.alert_radius_m}m geofence` : ""}
        </div>
      )}
      {t === "create_chore" && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {p.recurrence_rule ? p.recurrence_rule : "one-off"}
          {p.reward_stars ? ` · ${p.reward_stars}★` : ""}
        </div>
      )}
      {t === "create_poll" && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {(p.options || []).join(" · ")}
        </div>
      )}
      {t === "create_todo" && (
        <>
          <div className="text-xs text-muted-foreground mt-0.5">
            {p.list_hint ? `${String(p.list_hint).charAt(0).toUpperCase() + String(p.list_hint).slice(1)} list` : "Inbox"}
            {p.due_at ? ` · ${fmtRelativeDate(p.due_at)}` : ""}
          </div>
          {(p.checklist || []).length > 0 && (
            <ul className="mt-1.5 ml-1 text-[12px] text-muted-foreground space-y-0.5">
              {p.checklist.slice(0, verbose ? 8 : 6).map((c, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                  {typeof c === "string" ? c : c.title}
                </li>
              ))}
              {p.checklist.length > (verbose ? 8 : 6) && (
                <li className="text-[11px] italic">+{p.checklist.length - (verbose ? 8 : 6)} more</li>
              )}
            </ul>
          )}
        </>
      )}
      {/* "For:" line — who Kinly assigned this to */}
      <AssigneeLine
        participants={participants}
        assignees={assignees}
        todoAssignee={todoAssignee}
      />
    </li>
  );
}

function AssigneeLine({ participants, assignees, todoAssignee }) {
  const people = todoAssignee ? [todoAssignee] : (assignees.length ? assignees : participants);
  if (!people || people.length === 0) return null;
  return (
    <div data-testid="action-assignee" className="flex items-center gap-1.5 mt-1.5 text-[11px]">
      <span className="uppercase tracking-[0.18em] text-muted-foreground font-medium">For</span>
      <div className="flex items-center gap-1">
        {people.slice(0, 4).map((m) => (
          <span key={m.id} className="inline-flex items-center gap-1 pl-0.5 pr-2 h-6 rounded-full bg-card border border-border/70">
            <MemberAvatar member={m} size={18} ring={false} />
            <span className="text-foreground">{m.display_name?.split(" ")[0]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
